import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Clock, User, BookOpen, Repeat } from "lucide-react";
import coverFollowUp from "@/assets/cover-follow-up-que-converte.jpg";

const FollowUpQueConverte = () => {
  const navigate = useNavigate();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline:
      "Follow-up Que Converte: Como Fazer Follow-up em Vendas e Quando Insistir no Cliente",
    description:
      "Como fazer follow-up em vendas sem ser chato: cadência de contatos, canais, mensagens prontas e o ponto exato entre insistir no cliente e saber encerrar.",
    image: "https://bianchimbusiness.com.br/cover-follow-up-que-converte.jpg",
    datePublished: "2026-09-10",
    author: { "@type": "Person", name: "Junior Bianchim" },
    publisher: {
      "@type": "Organization",
      name: "Bianchim Business",
      logo: { "@type": "ImageObject", url: "https://bianchimbusiness.com.br/logo.png" },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://bianchimbusiness.com.br/posts/follow-up-que-converte",
    },
  };

  const cadencia = [
    {
      dia: "D0 — no fim da reunião",
      acao: "Feche o próximo passo com data e hora marcadas. O follow-up já nasce combinado.",
      exemplo: "Fechado então: te mando a proposta hoje e quinta às 10h a gente conversa sobre ela?",
    },
    {
      dia: "D1 — envio da proposta",
      acao: "Mande o material com um resumo de 3 linhas do que foi combinado, não só o anexo.",
      exemplo: "Conforme conversamos: 5 licenças, implantação em 2 semanas, condição válida até dia 20. Detalhes no PDF anexo.",
    },
    {
      dia: "D3 — primeiro retorno",
      acao: "Pergunta simples de acompanhamento, sem pressão, com um gancho de valor.",
      exemplo: "Oi, Carlos! Conseguiu olhar a proposta? Separei um case de uma empresa do seu setor que acho que responde sua dúvida sobre implantação.",
    },
    {
      dia: "D7 — conteúdo de valor",
      acao: "Traga algo útil: benchmark, artigo, dado do mercado. Não pergunte 'e aí, decidiu?'.",
      exemplo: "Saiu uma pesquisa do seu segmento que tem tudo a ver com o que conversamos. Te mando aqui — o ponto 3 é o que mais impacta o seu caso.",
    },
    {
      dia: "D14 — follow-up de decisão",
      acao: "Coloque a decisão na mesa com honestidade e ofereça caminhos.",
      exemplo: "Carlos, faz sentido seguirmos com o projeto neste mês ou a prioridade mudou? Se mudou, sem problema — me diz o novo timing que eu me organizo.",
    },
    {
      dia: "D21 — e-mail de encerramento",
      acao: "Mensagem de despedida elegante que deixa a porta aberta. É a que mais responde.",
      exemplo: "Vou parar de te incomodar por aqui. Se o projeto voltar ao radar, é só me chamar. Sucesso com a operação!",
    },
  ];

  const sinais = [
    {
      sinal: "Insista",
      cenario: "O cliente responde, mas demora",
      leitura: "Há interesse, mas a prioridade dele não é você. Diminua a frequência, aumente o valor de cada contato.",
    },
    {
      sinal: "Insista",
      cenario: "Pediu um adiamento com motivo claro",
      leitura: "'Volta comigo em janeiro por causa do orçamento' é um compromisso. Anote, confirme por escrito e retome na data.",
    },
    {
      sinal: "Insista",
      cenario: "Faz perguntas técnicas e de implantação",
      leitura: "Quem pergunta 'como funciona depois que compra' está se imaginando usando. É o momento de acelerar, não de esperar.",
    },
    {
      sinal: "Encerre",
      cenario: "Silêncio total após 5 ou 6 contatos em canais diferentes",
      leitura: "Silêncio prolongado é uma resposta. Mande o e-mail de encerramento e tire a oportunidade da previsão.",
    },
    {
      sinal: "Encerre",
      cenario: "Objeção de fit real e confirmada",
      leitura: "Se o cliente não tem verba, perfil ou problema que você resolve, insistir só queima sua reputação. Desqualifique e siga.",
    },
    {
      sinal: "Encerre",
      cenario: "Disse 'não' de forma clara e educada",
      leitura: "Respeite o não. Um 'não' respeitado hoje vira indicação ou retorno em seis meses. Um 'não' ignorado vira bloqueio no WhatsApp.",
    },
  ];

  return (
    <>
      <SEOHead
        title="Follow-up Que Converte: Como Fazer Follow-up em Vendas e Quando Insistir | Bianchim Business"
        description="Como fazer follow-up em vendas sem ser chato: cadência de contatos, canais, mensagens prontas e o ponto exato entre insistir no cliente e saber encerrar."
        keywords="como fazer follow-up, follow-up vendas, quando insistir no cliente, follow-up de vendas, cadência de follow-up, mensagem de follow-up, follow-up pós-proposta, vendas B2B"
        canonicalUrl="https://bianchimbusiness.com.br/posts/follow-up-que-converte"
        ogType="article"
        articlePublishedTime="2026-09-10"
        articleAuthor="Junior Bianchim"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <div className="min-h-screen bg-background">
        <Header />

        <main>
          <article>
            {/* Hero */}
            <section className="relative">
              <div className="w-full h-[46vh] sm:h-[58vh] relative overflow-hidden">
                <img
                  src={coverFollowUp}
                  alt="Vendedora em ligação de follow-up com quadro de cadência de contatos ao fundo"
                  className="w-full h-full object-cover"
                  width={1536}
                  height={1024}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
              </div>
              <div className="container mx-auto px-4 relative -mt-28 sm:-mt-40 z-10">
                <div className="max-w-4xl mx-auto">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary text-sm font-semibold mb-4">
                    Técnicas de Vendas
                  </span>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-4 text-foreground">
                    Follow-up que converte:{" "}
                    <span className="text-gradient-brand">
                      como fazer follow-up em vendas e quando insistir no cliente
                    </span>
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" /> <span>Junior Bianchim</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" /> <span>11 min de leitura</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" /> <span>10 Set 2026</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Conteúdo */}
            <section className="container mx-auto px-4 py-12">
              <div className="max-w-4xl mx-auto space-y-8 text-base sm:text-lg leading-relaxed text-muted-foreground">
                <p>
                  A maioria das vendas não se perde na apresentação. Se perde no silêncio que vem
                  depois dela. O vendedor faz uma reunião boa, manda a proposta e... espera. Liga uma
                  vez, manda um "passando para saber se deu uma olhada", não recebe resposta e
                  desiste. Do outro lado, o cliente não disse não. Ele só estava apagando incêndio,
                  esperando o chefe voltar de viagem, ou avaliando mais dois concorrentes.
                </p>
                <p>
                  Follow-up é a disciplina de manter a conversa viva até a decisão. Feito errado,
                  vira perseguição. Feito certo, é o que separa o vendedor que bate meta do que vive
                  reclamando que "o cliente sumiu". Neste artigo você vai ver como fazer follow-up
                  com cadência clara, o que escrever em cada contato e, principalmente, onde fica a
                  linha entre insistir no cliente e respeitar o momento dele.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  1. O follow-up começa antes do primeiro follow-up
                </h2>
                <p>
                  O erro número um do follow-up acontece ainda dentro da reunião: sair dela sem
                  próximo passo combinado. Quando o vendedor termina com "vou te mandar a proposta e
                  qualquer coisa a gente se fala", ele assinou a sentença de morte da oportunidade.
                  A partir dali, todo contato será uma interrupção.
                </p>
                <p>
                  Compare com quem termina assim: "Te mando a proposta até amanhã de manhã. Posso te
                  ligar quinta às 10h para ouvir o que você achou?". Agora o follow-up não é
                  interrupção — é um compromisso que o próprio cliente aceitou. A taxa de atendimento
                  dessa ligação é brutalmente maior. Regra prática: nenhuma reunião termina sem data,
                  hora e motivo do próximo contato definidos.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  2. Cadência de follow-up: o ritmo que mantém a porta aberta
                </h2>
                <p>
                  Follow-up sem cadência vira loteria: o vendedor lembra do cliente quando dá, manda
                  mensagem quando bate a ansiedade da meta. Cadência é um roteiro de contatos com
                  intervalos, canais e objetivos definidos. Esta é uma cadência padrão para vendas
                  consultivas após o envio da proposta — ajuste os intervalos ao seu ciclo:
                </p>

                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-muted/50 text-foreground">
                      <tr>
                        <th className="p-3 font-semibold">Momento</th>
                        <th className="p-3 font-semibold">O que fazer</th>
                        <th className="p-3 font-semibold">Exemplo de mensagem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cadencia.map((c) => (
                        <tr key={c.dia} className="border-t border-border align-top">
                          <td className="p-3 font-medium text-foreground">{c.dia}</td>
                          <td className="p-3">{c.acao}</td>
                          <td className="p-3 italic">"{c.exemplo}"</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p>
                  Note que nenhuma dessas mensagens é "e aí, decidiu?". Todo contato entrega algo: um
                  resumo, um case, um dado, uma pergunta honesta. O cliente que recebe valor em cada
                  toque não bloqueia o vendedor — ele responde quando o timing chega. E repare no
                  último contato: o e-mail de encerramento é, ironicamente, o que mais gera resposta.
                  Quando o cliente sente que vai perder a atenção, ele decide.
                </p>
                <p>
                  Essa cadência precisa estar registrada no seu CRM e conectada às etapas do{" "}
                  <Link to="/posts/gestao-de-pipeline" className="text-primary underline">
                    funil de vendas
                  </Link>
                  . Uma oportunidade parada há mais de 14 dias sem contato agendado não deveria constar
                  na sua previsão de receita.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  3. O canal certo na hora certa
                </h2>
                <p>
                  Alternar canais não é desespero, é estratégia. Cada canal tem uma função no
                  follow-up: o e-mail formaliza, o WhatsApp agiliza, a ligação aprofunda e o LinkedIn
                  mantém você presente sem pedir nada. A sequência inteligente alterna entre eles —
                  se mandou e-mail no dia 1, o retorno do dia 3 pode ser uma mensagem curta no
                  WhatsApp, e o dia 7, uma ligação.
                </p>
                <p>
                  A ligação é o canal mais evitado e o mais eficaz. Mensagem de texto permite que o
                  cliente adie a decisão indefinidamente com um emoji. Na ligação, a conversa anda: a
                  objeção real aparece, o "deixa eu ver com meu sócio" vira "meu sócio está aqui,
                  quer participar?". Reserve as ligações para os momentos de decisão e use texto para
                  os contatos de manutenção.
                </p>

                <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 to-accent/10 p-6 sm:p-8 mt-10">
                  <div className="flex items-center gap-2 mb-3 text-primary">
                    <Repeat className="w-5 h-5" />
                    <span className="font-semibold">Follow-up bom se treina, não se improvisa.</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3">
                    No Vendas Trainer você simula clientes que "somem" e aprende a trazer de volta
                  </h3>
                  <p className="mb-5">
                    Treine follow-up com personas de IA que adiam, pedem desconto e testam sua
                    persistência — como clientes reais. Cada simulação termina com nota de 0 a 100 e
                    feedback sobre timing, tom e argumentação.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={() => navigate("/auth?mode=signup")}
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Criar conta grátis e treinar follow-up
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                    <Button variant="outline" onClick={() => navigate("/calculadora-roi")}>
                      Calcular o ROI do treinamento
                    </Button>
                  </div>
                </div>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  4. Quando insistir no cliente — e quando encerrar
                </h2>
                <p>
                  Esta é a pergunta que todo vendedor faz e quase ninguém responde com critério.
                  Insistência não é questão de quantidade de contatos, é questão de sinais. O cliente
                  comunica, o tempo todo, se está frio ou apenas ocupado. Quem lê os sinais sabe
                  exatamente onde colocar energia.
                </p>

                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-muted/50 text-foreground">
                      <tr>
                        <th className="p-3 font-semibold">Decisão</th>
                        <th className="p-3 font-semibold">Cenário</th>
                        <th className="p-3 font-semibold">Como ler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sinais.map((s, i) => (
                        <tr key={i} className="border-t border-border align-top">
                          <td className="p-3 font-medium text-foreground">{s.sinal}</td>
                          <td className="p-3">{s.cenario}</td>
                          <td className="p-3">{s.leitura}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p>
                  Um número que ajuda a dar disciplina: a maior parte dos vendedores desiste depois de
                  um ou dois contatos, enquanto boa parte das vendas consultivas fecha entre o quarto
                  e o oitavo toque. Ou seja, o problema quase nunca é insistir demais — é insistir
                  errado: contatos sem valor, no canal errado, sem próximo passo. Insistir certo é
                  persistência; insistir errado é chateação.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  5. Os três erros que matam o follow-up
                </h2>
                <p>
                  O primeiro erro é o follow-up vazio: "só passando para saber se viu a proposta".
                  Essa mensagem não dá ao cliente nenhum motivo para responder. Substitua sempre por
                  contato com gancho: um case, um dado, uma pergunta específica sobre a decisão.
                </p>
                <p>
                  O segundo erro é o follow-up apressado: ligar no dia seguinte três vezes, cobrar
                  retorno antes do prazo que o próprio cliente pediu. Apressar comunica insegurança e
                  desespero de meta. Respeitar o timing combinado comunica profissionalismo — e quem
                  parece profissional ganha o benefício da dúvida.
                </p>
                <p>
                  O terceiro erro é o follow-up sem registro: o vendedor não anota o que foi dito,
                  manda a mesma mensagem duas vezes, esquece o motivo do adiamento. Follow-up é
                  memória. Cada contato deve começar de onde o anterior parou. É exatamente aqui que
                  um bom{" "}
                  <Link to="/posts/playbook-de-vendas" className="text-primary underline">
                    playbook de vendas
                  </Link>{" "}
                  ajuda: ele define cadência padrão, modelos de mensagem e critérios de encerramento
                  para o time inteiro, tirando o follow-up da cabeça de cada vendedor e colocando no
                  processo.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  Conclusão: follow-up é respeito ao tempo do cliente e ao seu
                </h2>
                <p>
                  O cliente que some quase nunca disse não. Ele disse "agora não dá para decidir".
                  Cabe a você manter a conversa viva com valor, cadência e honestidade até que o
                  momento dele chegue — ou até que os sinais mostrem que é hora de encerrar com
                  elegância e abrir espaço na agenda para quem está pronto.
                </p>
                <p>
                  Comece hoje com duas mudanças simples: nunca termine uma reunião sem próximo passo
                  marcado e transforme sua cadência em processo escrito. Em poucas semanas, sua taxa
                  de resposta muda — e junto com ela, o seu funil.
                </p>

                <PageNavigation />
              </div>
            </section>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default FollowUpQueConverte;
